import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DocumentTemplateRow, Nullable } from '@/Types';
import { asyncActions } from './asyncActions';
import { DocumentInfoForPrintModel } from '@/Models/DocumentInfoModel';
import { NumberGenerationType } from '@/Enums';
import { documentTemplateMapping } from '@/Mapping/DocumentTemplateMapping';

export type SetCountCopy = {
  guid: string;
  count: number;
};

export type SetIsSortWhenPrinting = {
  guid: string;
  isSort: boolean;
};

export type GroupedDocumentNumbers = {
  orderId: number;
  numbers: string[];
};

export enum DocumentPrintStep {
  None = 'None',
  CreateDocument = 'CreateDocument',
  SingleDocumentPrint = 'SingleDocumentPrint',
  SelectTemplate = 'SelectTemplate',
  SelectDocumentNumber = 'SelectDocumentNumber',
  EmailPreview = 'EmailPreview',
  TemplateNotExists = 'TemplateNotExists',
  Close = 'Close',
}

export type DocumentPrintModalState = {
  documentType: Nullable<NumberGenerationType>;
  currentStep: DocumentPrintStep;
  documentTemplates: DocumentTemplateRow[];
  selectedDocumentTemplate: Nullable<DocumentTemplateRow>;
  selectedTypeNumbers: GroupedDocumentNumbers[];
  documentNumbersInfo: DocumentInfoForPrintModel[];
  selectedNumbersInfo: DocumentInfoForPrintModel[];
};

const initialState: DocumentPrintModalState = {
  documentType: null,
  currentStep: DocumentPrintStep.None,
  documentTemplates: [],
  selectedDocumentTemplate: null,

  selectedTypeNumbers: [],
  documentNumbersInfo: [],
  selectedNumbersInfo: [],
};

const documentPrintSlice = createSlice({
  name: 'documentPrintModalSlice',
  initialState,
  reducers: {
    setDocumentType(state, action: PayloadAction<Nullable<NumberGenerationType>>) {
      state.documentType = action.payload;
    },
    setDocumentPrintStep(state, action: PayloadAction<DocumentPrintStep>) {
      state.currentStep = action.payload;
    },
    setSelectedTemplate(state, action: PayloadAction<DocumentTemplateRow>) {
      state.selectedDocumentTemplate = action.payload;
    },
    setSelectedTypeNumbers(state, action: PayloadAction<GroupedDocumentNumbers[]>) {
      state.selectedTypeNumbers = action.payload;
    },
    setSelectedDocumentNumbers(state, action: PayloadAction<DocumentInfoForPrintModel[]>) {
      state.selectedNumbersInfo = action.payload;
    },
    setCountCopy(state, action: PayloadAction<SetCountCopy>) {
      const { count, guid } = action.payload;

      const row = state.documentTemplates.find((x) => x.guid === guid);
      if (row) {
        row.countCopy = count;
      }

      if (state.selectedDocumentTemplate?.guid === guid) {
        state.selectedDocumentTemplate.countCopy = count;
      }
    },
    setIsSortWhenPrinting(state, action: PayloadAction<SetIsSortWhenPrinting>) {
      const { isSort, guid } = action.payload;

      const row = state.documentTemplates.find((x) => x.guid == guid);
      if (row) {
        row.isSortWhenPrinting = isSort;
      }

      if (state.selectedDocumentTemplate?.guid === guid) {
        state.selectedDocumentTemplate.isSortWhenPrinting = isSort;
      }
    },
    setGenerateNumberCountCopy(state, action: PayloadAction<SetCountCopy>) {
      const { count, guid } = action.payload;

      const row = state.documentTemplates.find((x) => x.guid === guid);
      if (row) {
        row.generateNumberCountCopy = count;
      }
    },
    setGenerateNumberSortWhenPrinting(state, action: PayloadAction<SetIsSortWhenPrinting>) {
      const { isSort, guid } = action.payload;

      const row = state.documentTemplates.find((x) => x.guid == guid);
      if (row) {
        row.generateNumberSortWhenPrinting = isSort;
      }
    },
    setGenerateNumberPrintDocument(state, action: PayloadAction<{ guid: string; print: boolean }>) {
      const { print, guid } = action.payload;

      const row = state.documentTemplates.find((x) => x.guid === guid);
      if (row) {
        row.generateNumberPrintDocument = print;
      }
    },
    setDocumentNumberCountCopy(state, action: PayloadAction<SetCountCopy>) {
      const { count, guid } = action.payload;

      const row = state.documentNumbersInfo.find((x) => x.documentNumber === guid);
      const selectedRow = state.selectedNumbersInfo.find((x) => x.documentNumber === guid);

      if (row) {
        row.countCopy = count;
      }

      if (selectedRow) {
        selectedRow.countCopy = count;
      }
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(asyncActions.getDocumentTemplates.fulfilled, (state, action) => {
      state.documentTemplates = action.payload.templates.map((x) => {
        return {
          guid: x.guid,
          name: x.name,
          countCopy: x.documentTemplateSettings.countCopy || 1,
          isSortWhenPrinting: x.documentTemplateSettings.isSortWhenPrinting,
          numberGenerationType: documentTemplateMapping.convertDocumentTypeToNumberGenerationType(
            x.automaticallyGeneratedDocumentType,
          ),
          generateNumberCountCopy: x.generatingNumberSettings?.countCopy,
          generateNumberSortWhenPrinting: x.generatingNumberSettings?.isSortWhenPrinting,
          generateNumberPrintDocument: x.generatingNumberSettings?.printDocument,
          generateNumberSendOrderEmail: x.generatingNumberSettings?.sendOrderEmail,
        };
      });
    });

    builder.addCase(asyncActions.getDocumentsForPrint.fulfilled, (state, action) => {
      state.documentNumbersInfo = action.payload.models.map((item) => ({
        ...item,
        countCopy: state.selectedDocumentTemplate?.countCopy ?? 1,
      }));
    });
  },
});

const { actions, reducer } = documentPrintSlice;

export const documentPrintModalReducer = reducer;
export const documentPrintModalActions = actions;
export const documentPrintModalAsyncActions = asyncActions;
